import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { EmptyList, Pagination, Spinner, WithRoleAccess } from "@services/ui-components";
import { ApolloError } from "@apollo/client";
import ErrorService from "../../../../services/ErrorService";
import {
    CompanyAccess,
    StorageProductRelationHistoryAction,
    StorageProductRelationHistoryProxy,
} from "@me-team/host/main/graphql/types";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, OverlayTrigger, Tooltip, Popover, CloseButton } from "react-bootstrap";
import {
    useGetWarehousesOperationsQuery,
    useGetWarehousesQuery,
} from "@me-team/host/main/warehouses/graphql/warehouses.hooks";
import { useToast } from "../../../../hooks/useToast";
import { useReactiveVar } from "@apollo/client";
import { BookingDataForSale, IOperationsFilter } from "../types";
import { formatDate, formatDateDMY, formatTime } from "@me-team/host/src/utils/utils";
import HistoryIcon from "../img/historyIcon.svg";
import WriteOffModal from "@me-team/common/src/components/WarehousesGoods/modals/WriteOffModal";
import MoveGoodsModal from "@me-team/common/src/components/WarehousesGoods/modals/MoveGoodsModal";
import AddGoodsWarehouseModal from "@me-team/common/src/components/WarehousesGoods/modals/AddGoodsWarehouseModal";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import { ChosenProductType } from "@me-team/common/src/components/Warehouse/modals/modalsUtils";
import RestoreWarehouseModal from "../modals/RestoreWarehouseModal";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import useScrollToTopOnChange from "../../../../hooks/useScrollToTopOnChange";
import { SaleProducts } from "../../../index";
import {
    getCurrentStepFromLocalStorage,
    getWarehouseTutorialType,
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import WarehouseJoyrideWrapper from "../../../Joyride/WarehouseTutorial/WarehouseJoyrideWrapper";
import { useCloseTooltipsOnModalOpen } from "../../../../hooks/useCloseTooltipsOnModalOpen";
import { removeHandler, setHandler } from "../../../Joyride/WarehouseTutorial/utils/handlerRegistry";
import { insertLineBreakBeforeParen } from "../helpers";
import { useWarehouseOperationsAccess } from "../useWarehouseOperationsAccess";

interface IWarehousesOerationsTableProps {
    filters: IOperationsFilter;
}

const WarehousesOerationsTable: React.FC<IWarehousesOerationsTableProps> = ({ filters }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const navigate = useNavigate();
    const { canViewOperationHistory, canDeleteRestoreWarehouse, canEditOperation } = useWarehouseOperationsAccess();
    const tutorialActive = isWarehouseTutorialActive();
    const tutorialType = getWarehouseTutorialType();
    const startStep = getCurrentStepFromLocalStorage();
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [restoreStorage, setRestoreStorage] = useState(null);
    const [chosenProductAfterStorageRestore, setChosenProductAfterStorageRestore] =
        useState<StorageProductRelationHistoryProxy>(null);
    const [bookingUuidForSale, setBookingUuidForSale] = useState<string>(null);

    useScrollToTopOnChange(currentPage);

    const [chosenProduct, setChosenProduct] = useState<StorageProductRelationHistoryProxy>(null);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();

    const resetCurrentPage = () => {
        setCurrentPage(1);
    };

    useEffect(() => {
        resetCurrentPage();
    }, [filters]);

    const formatType = (value?: string | null): string => {
        return value ? value.replace(/_/g, "-") : null;
    };
    const {
        data: warehousesOperations,
        loading: loadingwarehousesOperations,
        refetch,
    } = useGetWarehousesOperationsQuery({
        variables: {
            page: currentPage,
            itemsPerPage: itemsPerPage,
            companyId: +currentCompanyId,
            currentBranchId: +branchId,
            action: formatType(filters.action),
            supplier: filters.supplier,
            storageId: filters.storageId,
            productName: filters.productName,
            productCategory: filters.productCategory,
            edited: filters.edited,
            remnants: filters.remnants,
            startDate: filters.startDate,
            endDate: filters.endDate,
        },
        skip: !currentCompanyId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data, loading: loadingWarehouses } = useGetWarehousesQuery({
        variables: {
            page: 1,
            itemsPerPage: 10000,
            currentBranchId: +branchId,
            companyId: +currentCompanyId,
        },
        skip: !currentCompanyId || !branchId,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const warehousesOperationsEntries =
        warehousesOperations?.user?.company?.branchesPaginated?.branches[0]?.storageProductRelationHistoryPaginated
            ?.entities;
    const pageCount =
        warehousesOperations?.user?.company?.branchesPaginated?.branches[0]?.storageProductRelationHistoryPaginated
            ?.pageCount;
    const totalItems =
        warehousesOperations?.user?.company?.branchesPaginated?.branches[0]?.storageProductRelationHistoryPaginated
            ?.totalCount;
    const warehouses = data?.user?.company?.branchesPaginated?.branches[0]?.storagesPaginated?.storages;
    const shouldShowActionsColumn =
        canViewOperationHistory || warehousesOperationsEntries?.some((product) => canEditOperation(product)) === true;

    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const writeOffReasons =
        warehousesOperations?.relations?.writeOffReasons?.map((i) => ({
            id: i?.id,
            name: i?.name,
        })) || [];

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleShowModal = (product: StorageProductRelationHistoryProxy) => {
        setChosenProduct(product);
    };

    const handleCloseModal = useCallback(() => {
        resetTooltips();
        setChosenProduct(null);
    }, [resetTooltips]);

    const handlerNextSteps = useCallback(() => {
        const step = getCurrentStepFromLocalStorage();
        if (step === 17 || step === 14) handleCloseModal();
    }, [handleCloseModal]);

    useEffect(() => {
        const id = "edit-operation-modal-handler";
        setHandler(id, handlerNextSteps);
        return () => removeHandler(id);
    }, [handlerNextSteps]);

    const OPERATION_ACTIONS: Record<
        StorageProductRelationHistoryAction,
        { label: string; icon: JSX.Element; sign?: string }
    > = {
        [StorageProductRelationHistoryAction.WriteOff]: {
            label: t("Write-off of goods"),
            icon: <i className="bi bi-arrow-up" />,
            sign: "-",
        },
        [StorageProductRelationHistoryAction.Arrival]: {
            label: t("Arrival of goods"),
            icon: <i className="bi bi-arrow-down" />,
            sign: "+",
        },
        [StorageProductRelationHistoryAction.Movement]: {
            label: t("Movement"),
            icon: <i className="bi bi-arrow-left-right" />,
        },
        [StorageProductRelationHistoryAction.Sale]: {
            label: t("Selling"),
            icon: <i className="bi bi-cash-coin" />,
            sign: "-",
        },
        [StorageProductRelationHistoryAction.BookingWriteOff]: {
            label: t("Booking write-off of goods"),
            icon: <i className="bi bi-arrow-up" />,
            sign: "-",
        },
    };

    const transformUnitsForSelect = () => {
        return warehouses
            ?.filter((i) => !i?.deleted)
            .map((i) => ({
                value: i?.id,
                label: i?.name,
            }));
    };

    const chosenProductAction = chosenProduct?.historyAction;
    const canEditChosenOperation = canEditOperation(chosenProduct);

    const renderStorageName = (storage?: StorageProductRelationHistoryProxy["storage"] | null) => {
        if (!storage?.name) return "-";

        return (
            <WithRoleAccess permission={CompanyAccess.WarehouseProductListView} fallback={<span>{storage?.name}</span>}>
                <Link
                    className="text-decoration-none text-dark"
                    to={`/warehouses/warehouses-goods/branch/${branchId}/${storage?.id}`}
                >
                    {storage?.name}
                </Link>
            </WithRoleAccess>
        );
    };

    const handleEditViewTransaction = (product: StorageProductRelationHistoryProxy) => {
        if (!canEditOperation(product)) return;

        setBookingUuidForSale(null);
        setChosenProductAfterStorageRestore(null);
        if (product?.storage.deleted) {
            if (!canDeleteRestoreWarehouse) return;

            setRestoreStorage(product?.storage);
            setChosenProductAfterStorageRestore(product);
            return;
        }
        if (product?.booking && product?.historyAction !== StorageProductRelationHistoryAction.Sale) {
            navigate(
                `/products/write-off/warehouse-operation/company/${currentCompanyId}/branch/${branchId}/bookingUuid/${product?.booking?.uuid}/edit/operationNumber/${product.operationNumber}`,
                {
                    state: {
                        comeFrom: {
                            isFromOperations: true,
                        },
                    },
                }
            );
            return;
        }
        if (product?.booking && product?.historyAction === StorageProductRelationHistoryAction.Sale) {
            setBookingUuidForSale(product?.booking?.uuid);
        }
        handleShowModal(product);
    };

    const bookingDataForSale: BookingDataForSale = useMemo(() => {
        if (!(chosenProduct?.booking && chosenProduct?.historyAction === StorageProductRelationHistoryAction.Sale)) {
            return null;
        }
        return {
            employee: chosenProduct?.employee?.id as number,
            clientName: chosenProduct?.client?.name ?? null,
            clientPhone: chosenProduct?.client?.phone ?? null,
            clientEmail: chosenProduct?.client?.email ?? null,
        };
    }, [
        chosenProduct?.employee?.id,
        chosenProduct?.client?.name,
        chosenProduct?.client?.phone,
        chosenProduct?.client?.email,
        chosenProduct?.booking,
        chosenProduct?.historyAction,
    ]);

    const handleCloseStorageModal = () => {
        resetTooltips();
        setRestoreStorage(null);
    };

    const handleRestoreStorageRefetch = async () => {
        handleCloseStorageModal();
        await refetch();
        handleEditViewTransaction({
            ...chosenProductAfterStorageRestore,
            storage: { ...chosenProductAfterStorageRestore?.storage, deleted: false },
        });
    };

    if (loadingwarehousesOperations || !currentCompanyId || loadingWarehouses) return <Spinner />;

    return (
        <>
            <div className="col-12 table-responsive scrollbar position-relative" data-tour="operations">
                {
                    <table className="table table-borderless">
                        <thead>
                            <tr className="fs-7">
                                <th className="py-2 px-2 align-middle">{t("Date and Time")}</th>
                                <th className="py-2 px-2 lign-middle">{t("Transaction number")}</th>
                                <th className="py-2 px-2 align-middle">{t("Operation type")}</th>
                                <th className="py-2 px-2 align-middle text-nowrap">{t("Supplier")}</th>
                                <th className="py-3 px-2 align-middle">{t("Warehouse")}</th>
                                <th className="py-2 px-2 align-middle">{t("Product name")}</th>
                                <th className="py-2 px-2 align-middle">{t("Amount")}</th>
                                <th className="py-2 px-2 align-middle">{t("Comment")}</th>
                                <th className="py-2 px-2 align-middle text-start">{t("Client visit")}</th>
                                {shouldShowActionsColumn ? <th className="py-3 px-2 align-middle"></th> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {warehousesOperationsEntries?.map((product: StorageProductRelationHistoryProxy) => {
                                const dateAndTime = formatDateDMY(product?.date);
                                const date = dateAndTime.split(" ")[0];
                                const time = dateAndTime.split(" ")[1];
                                const action = OPERATION_ACTIONS[product.historyAction];
                                const message = product?.comment || product?.writeOffReason?.name || "-";
                                const canEditCurrentOperation = canEditOperation(product);
                                const shouldShowRowActions = canViewOperationHistory || canEditCurrentOperation;

                                return (
                                    <tr key={product.id} className="border-bottom">
                                        <td className="align-middle  pe-2">
                                            {canEditCurrentOperation ? (
                                                <span
                                                    className="text-primary cursor-pointer"
                                                    onClick={() => handleEditViewTransaction(product)}
                                                >
                                                    {date}
                                                    <br />
                                                    {time}
                                                </span>
                                            ) : (
                                                <span>
                                                    {date}
                                                    <br />
                                                    {time}
                                                </span>
                                            )}
                                        </td>
                                        <td className="align-middle pe-2">{product?.operationNumberReadable || "-"}</td>
                                        <td className="align-middle pe-2">
                                            <div className="d-flex gap-2">
                                                {action?.icon}
                                                <div>{action?.label}</div>
                                            </div>
                                        </td>
                                        <td className="align-middle pe-2">{product?.supplier?.name || "-"}</td>
                                        <td className="align-middle pe-2">
                                            {product?.relationStorage?.name ? (
                                                <>
                                                    {renderStorageName(product?.storage)}{" "}
                                                    <i className="bi bi-arrow-right"></i>{" "}
                                                    {renderStorageName(product?.relationStorage)}
                                                </>
                                            ) : (
                                                renderStorageName(product?.storage)
                                            )}
                                        </td>
                                        <td className="align-middle pe-2">
                                            <WithRoleAccess
                                                permission={CompanyAccess.ProductEdit}
                                                fallback={<span>{product?.product?.name}</span>}
                                            >
                                                <Link
                                                    to={`/warehouse/product/${currentCompanyId}/${product?.product?.id}/edit-product/branch/${branchId}`}
                                                    className={`text-decoration-none text-primary`}
                                                >
                                                    {product?.product?.name}
                                                </Link>
                                            </WithRoleAccess>
                                        </td>
                                        <td className="align-middle pe-2">
                                            <span style={{ whiteSpace: "pre-line" }}>
                                                {action?.sign ??
                                                    (product?.historyAction ===
                                                    StorageProductRelationHistoryAction.WriteOff
                                                        ? "-"
                                                        : product?.historyAction ===
                                                            StorageProductRelationHistoryAction.Arrival
                                                          ? "+"
                                                          : "")}{" "}
                                                {insertLineBreakBeforeParen(product?.quantityForRender)}
                                            </span>
                                        </td>
                                        <td className="align-middle pe-2">
                                            {message.length > 30 ? (
                                                <OverlayTrigger
                                                    trigger="click"
                                                    placement={"bottom"}
                                                    rootClose
                                                    overlay={
                                                        <Popover>
                                                            <Popover.Body className="d-flex gap-3">
                                                                <div>{message}</div>
                                                                <CloseButton
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const backdrop =
                                                                            document.querySelector(".popover");
                                                                        if (backdrop) document.body.click();
                                                                    }}
                                                                />
                                                            </Popover.Body>
                                                        </Popover>
                                                    }
                                                >
                                                    <div className="cursor-pointer">{`${message.slice(0, 30)}...`}</div>
                                                </OverlayTrigger>
                                            ) : (
                                                message
                                            )}
                                        </td>
                                        <td className="align-middle">
                                            {product?.booking ? (
                                                <WithRoleAccess
                                                    anyPermissions={[
                                                        CompanyAccess.LogbookBookingEdit,
                                                        CompanyAccess.LogbookView,
                                                    ]}
                                                    fallback={
                                                        <span>
                                                            {formatDate(product?.booking?.date)}
                                                            <br />
                                                            {formatTime(product?.booking?.time)}
                                                        </span>
                                                    }
                                                >
                                                    <Link
                                                        className="text-decoration-none"
                                                        to={`/logbook/edit-record/${product?.booking?.uuid}/${branchId}`}
                                                    >
                                                        {formatDate(product?.booking?.date)}
                                                        <br />
                                                        {formatTime(product?.booking?.time)}
                                                    </Link>
                                                </WithRoleAccess>
                                            ) : (
                                                "-"
                                            )}
                                        </td>
                                        {shouldShowActionsColumn ? (
                                            <td className="align-middle">
                                                {shouldShowRowActions ? (
                                                    <div className="d-flex justify-content-end">
                                                        {canViewOperationHistory ? (
                                                            <OverlayTrigger
                                                                overlay={<Tooltip>{t("History of Changes")}</Tooltip>}
                                                            >
                                                                <Link
                                                                    to={`/warehouses/company/${currentCompanyId}/branch/${branchId}/warehouse-operations/${product.id}/change-history`}
                                                                    className="btn btn-outline-secondary text-dark m-1 p-0 d-flex justify-content-center align-items-center"
                                                                    style={{ height: "39px", width: "39px" }}
                                                                    data-tour="history-operation-btn"
                                                                    onClick={() =>
                                                                        tutorialActive &&
                                                                        moveStepInLocalStorage(StepDirection.NEXT)
                                                                    }
                                                                >
                                                                    <HistoryIcon />
                                                                </Link>
                                                            </OverlayTrigger>
                                                        ) : null}
                                                        {canEditCurrentOperation ? (
                                                            <OverlayTrigger
                                                                key={tooltipResetKey}
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t("Edit transaction", {
                                                                            operation: action?.label,
                                                                        })}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="outline-secondary text-dark m-1 p-0"
                                                                    onClick={() => {
                                                                        tutorialActive &&
                                                                            moveStepInLocalStorage(StepDirection.NEXT);
                                                                        handleEditViewTransaction(product);
                                                                    }}
                                                                    style={{ height: "39px", width: "39px" }}
                                                                    data-tour="edit-operation-btn"
                                                                >
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                }
            </div>
            {warehousesOperationsEntries?.length === 0 && <EmptyList title={t("No data found for the given values")} />}
            {warehousesOperationsEntries?.length !== 0 ? (
                <div className="d-flex justify-content-between align-items-center mt-4 mb-5">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : null}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            <WriteOffModal
                show={
                    chosenProductAction === StorageProductRelationHistoryAction.WriteOff ||
                    chosenProductAction === StorageProductRelationHistoryAction.BookingWriteOff
                }
                handleModal={handleCloseModal}
                refetch={refetch}
                chosenProduct={chosenProduct as ChosenProductType}
                writeOffReasons={writeOffReasons}
                isEditMode
                toggleShowToast={toggleShowToast}
                canEditOperation={canEditChosenOperation}
                dialogClassName={tutorialActive && "edit-operation"}
                centered={tutorialActive && false}
                dataTour="edit-operation-form"
            />
            <MoveGoodsModal
                show={chosenProductAction === StorageProductRelationHistoryAction.Movement}
                refetch={refetch}
                handleModal={handleCloseModal}
                chosenProduct={chosenProduct as ChosenProductType}
                warehouses={transformUnitsForSelect()}
                isEditMode
                toggleShowToast={toggleShowToast}
                canEditOperation={canEditChosenOperation}
                dialogClassName={tutorialActive && "edit-operation"}
                centered={tutorialActive && false}
                dataTour="edit-operation-form"
            />
            <AddGoodsWarehouseModal
                show={chosenProductAction === StorageProductRelationHistoryAction.Arrival}
                handleModal={handleCloseModal}
                chosenProduct={chosenProduct as ChosenProductType}
                refetch={refetch}
                isEditMode
                toggleShowToast={toggleShowToast}
                canEditOperation={canEditChosenOperation}
                dialogClassName={tutorialActive && "edit-operation"}
                centered={tutorialActive && false}
                dataTour="edit-operation-form"
            />
            <RestoreWarehouseModal
                show={!!restoreStorage}
                handleModal={handleCloseStorageModal}
                toggleShowToast={toggleShowToast}
                warehouse={restoreStorage}
                refetch={handleRestoreStorageRefetch}
                canDeleteRestoreWarehouse={canDeleteRestoreWarehouse}
            />

            {(tutorialActive || chosenProductAction === StorageProductRelationHistoryAction.Sale) && (
                <SaleProducts
                    showModal={chosenProductAction === StorageProductRelationHistoryAction.Sale}
                    action="update"
                    bookingDataForSale={bookingDataForSale}
                    bookingUuid={bookingUuidForSale}
                    chosenProduct={chosenProduct as ChosenProductType}
                    handleCloseModal={handleCloseModal}
                    toggleShowToast={toggleShowToast}
                    refetch={refetch}
                    canEditOperation={canEditChosenOperation}
                    dialogClassName={tutorialActive && "sale-product-modal"}
                    centeredModal={tutorialActive && false}
                    dataTour="edit-operation-form"
                />
            )}

            {tutorialActive && tutorialType && (
                <WarehouseJoyrideWrapper tutorialType={tutorialType} startStep={startStep} />
            )}

            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default WarehousesOerationsTable;
